import React, { useEffect, useRef, useState } from "react";

import { storefrontUrl } from "../../lib/storefront";
import { AvatarIcon, CaretIcon, CloseIcon } from "./icons";

/**
 * hanamaru `Navbar/AccountMenu.tsx`, the parts that are navigation.
 *
 * Every entry is a link into the storefront, where the account actually
 * lives. Log out, the NSFW switch and the admin toggles stay there: they need
 * hanamaru's session mutations, and a copy that half-works here would leave a
 * reader logged out on one app and in on the other.
 */
const ACCOUNT_LINKS = [
  { label: "My Account", path: "/user" },
  { label: "My Vouchers", path: "/user/my-voucher" },
  { label: "My Events", path: "/user/events" },
  { label: "Order History", path: "/user/history" },
  { label: "Point History", path: "/user/friendship-points" },
  { label: "Address Book", path: "/user/address" },
  { label: "Unique Request", path: "/unique-request" },
  { label: "Wishlist", path: "/user/wishlist", mobileOnly: true }
];

const Avatar = ({ user, size }) =>
  user && user.avatarUrl ? (
    <img className="site-avatar" src={user.avatarUrl} alt="" width={size} height={size} />
  ) : (
    <AvatarIcon className="site-avatar" />
  );

const MenuInner = ({ user }) => (
  <div className="site-menu">
    <div className="site-menu__profile">
      <span className="site-menu__avatar">
        <Avatar user={user} size={40} />
      </span>
      <div className="site-menu__who">
        <span className="site-menu__name">{user ? user.name : "Guest"}</span>
        {user ? <span className="site-menu__username">{user.username}</span> : null}
      </div>
    </div>

    {user ? (
      <>
        <div className="site-menu__points">
          <div>
            <span className="site-menu__points-label">Friendship Points</span>
            <span className="site-menu__points-value">{user.points.toLocaleString("id-ID")} Points</span>
          </div>
          <a className="site-menu__fp-shop" href={storefrontUrl("/friendship-points")}>
            FP Shop
          </a>
        </div>
        <ul className="site-menu__links">
          {ACCOUNT_LINKS.map(link => (
            <li key={link.path} className={link.mobileOnly ? "site-menu__item--mobile" : null}>
              <a className="site-menu__link" href={storefrontUrl(link.path)}>
                {link.label}
              </a>
            </li>
          ))}
        </ul>
      </>
    ) : (
      <ul className="site-menu__links">
        <li>
          <a className="site-menu__link" href={storefrontUrl("/login")}>
            Login
          </a>
        </li>
      </ul>
    )}
  </div>
);

/** Desktop: "Sign in", or "Halo, {first name}" opening a dropdown. */
export const DesktopAccount = ({ user, isLoading }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const closeOutside = e => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const closeOnEscape = e => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", closeOutside);
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOutside);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  if (isLoading || user === null) {
    return (
      <a className="site-account__signin" href={storefrontUrl("/login")}>
        {isLoading ? "Loading..." : "Sign in"}
      </a>
    );
  }

  const toggle = () => setOpen(wasOpen => !wasOpen);

  return (
    <div className="site-account" ref={rootRef}>
      <button type="button" className="site-account__button" onClick={toggle} aria-expanded={open} aria-haspopup="menu">
        <span className="site-account__avatar">
          <Avatar user={user} size={24} />
        </span>
        <span>{`Halo, ${user.name.split(" ")[0]}`}</span>
        <CaretIcon className="site-account__caret" />
      </button>
      {open ? (
        <div className="site-account__dropdown">
          <MenuInner user={user} />
        </div>
      ) : null}
    </div>
  );
};

/** Phones: the hamburger's full-screen "Account" panel. */
export const MobileAccountDrawer = ({ open, user, onClose }) => {
  useEffect(() => {
    if (!open) return undefined;

    const closeOnEscape = e => {
      if (e.key === "Escape") onClose();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="site-drawer" role="dialog" aria-modal="true" aria-label="Account">
      <div className="site-drawer__bar">
        <button type="button" className="site-icon-btn" onClick={onClose} aria-label="Tutup menu">
          <CloseIcon />
        </button>
        <span>Account</span>
      </div>
      <div className="site-drawer__body">
        <MenuInner user={user} />
      </div>
    </div>
  );
};
